import { Button, Divider, Grid, Paper, Snackbar, Typography } from '@material-ui/core';
import BackupIcon from '@material-ui/icons/Backup';
import React from 'react';

import { uploadMonitorCSVfile } from '~/src/dash/api/console-api';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import { ModelSnackBarProgressBarContainer } from '~/src/dash/components/model-snackbar-progresbar-container';
import { IStore } from '~/src/dash/reducers/store';
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import { genDownloadMonitorCSVURL } from '~/src/dash/router/routes';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import UploadManager, { EUploadResponseType, IUploadStatus } from '~/src/dash/utils/upload_manager';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState extends IBaseState {
  snackbarMessage: string;
  snackbarError: boolean;
  count: number;
  updating: boolean;
  loading: boolean;

  csvUploadErrors: string;
  rowErrors: string[];
}

export const MonitorBulkUploadView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      textField: {
        margin: 0,
        minWidth: '400px',
        width: '30%',
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      updating: false,
      loading: false,
      count: 0,

      snackbarMessage: '',
      snackbarError: false,

      csvUploadErrors: '',
      rowErrors: [] as string[],
    };

    uploadManager = new UploadManager();
    uploadKeyToIdx = new Map<string, { idx: number }>();

    componentDidMount() {
      this.uploadManager.events.addListener(
        'uploadManager_uploadStatusChange',
        (item: IUploadStatus) => {
          if (item.status === EUploadResponseType.ABORTED) {
            if (this.uploadKeyToIdx.has(item.key)) {
              const itemLocation = this.uploadKeyToIdx.get(item.key);
              if (itemLocation === undefined) {
                return;
              }
            }
          }
        },
      );
    }

    componentWillUnmount() {
      this.uploadManager.cleanup();
    }

    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({
        [target.name]: target.value,
      });
    };

    uploadCSV = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        const filenameParams = file.name.split('.');
        const ext = filenameParams[filenameParams.length - 1];
        if (ext.toLowerCase() !== 'csv') {
          this.setState({
            snackbarMessage: 'Only CSV files are supported',
            snackbarError: true,
          });
          return;
        }
        target.value = '';
        const uploadKey = this.uploadManager.addUpload(file.name);

        uploadMonitorCSVfile({
          publisher_id: this.props.session.publisher.ID,
          name: filenameParams[0],
          file,
          managerFunc: (xhr: XMLHttpRequest) => {
            this.uploadManager.addListenersToUpload(uploadKey, xhr);
          },
        })
          .then((json: any) => {
            if (json && json.response && json.response.success) {
              try {
                this.uploadManager.signalSuccess(uploadKey);
              } catch (e) {
                this.uploadManager.signalFailure(uploadKey);
              }
            } else {
              this.uploadManager.signalFailure(uploadKey);
            }
            if (json && json.response && json.response.errors && json.response.errors.length > 0) {
              this.setState({
                csvUploadErrors: json.response.errors,
                rowErrors: json.response.row_errors || [],
              });
            } else {
              this.setState({
                csvUploadErrors: '',
                rowErrors: [],
              });
            }
          })
          .catch(() => {
            this.uploadManager.signalFailure(uploadKey);
          });
      }
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
      });
    };

    render() {
      const { classes } = this.props;
      // @ts-ignore
      return (
        <div>
          <Paper classes={{ root: classes.paper }}>
            <Grid
              container
              alignItems='center'
              style={{ paddingTop: '25px', paddingBottom: '10px' }}
            >
              <Grid item spacing={0} style={{ paddingRight: 20 }}>
                <ModelMediumButton
                  variant='contained'
                  href={genDownloadMonitorCSVURL(this.props.session.publisher.ID)}
                >
                  Download New CSV
                </ModelMediumButton>
              </Grid>
              <Grid item>
                <Button
                  component='label'
                  style={{
                    paddingTop: '18px',
                    paddingBottom: '18px',
                    color: getColorTheme('primaryButtons'),
                  }}
                >
                  <BackupIcon />
                  &nbsp;&nbsp;Upload CSV
                  <input
                    type='file'
                    style={{ display: 'none', marginLeft: '20px' }}
                    accept='.csv'
                    onChange={this.uploadCSV}
                  />
                </Button>
              </Grid>
            </Grid>
          </Paper>
          {/* MIDDLE MAIN - Errors  */}
          {this.state.csvUploadErrors.length ? (
            <Paper classes={{ root: classes.paper }}>
              <Typography variant='h5'>CSV Upload Errors</Typography>
              <Divider style={{ marginTop: 15, marginBottom: 15 }} />
              <Typography style={{ fontWeight: 450 }}>{this.state.csvUploadErrors}:</Typography>
              {this.state.rowErrors.map((error: string) => {
                // errors are semicolon delimited
                // example error: error on row 3: site ID cannot be empty;;;Demand source (bidder type) invalid"
                return (
                  <>
                    <Typography style={{ fontWeight: 450 }}>
                      {error.substr(0, error.indexOf(':'))}
                    </Typography>
                    <ul style={{ marginTop: 0 }}>
                      {error
                        .substr(error.indexOf(':') + 1)
                        .replace(/\n/g, '')
                        .split(';')
                        .filter((m: string) => !!m)
                        .map((m: string) => {
                          return <li>{m}</li>;
                        })}
                    </ul>
                    <br />
                  </>
                );
              })}
            </Paper>
          ) : (
            React.Fragment
          )}
          <Snackbar
            open={true}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'bottom',
            }}
          >
            <ModelSnackBarProgressBarContainer uploadManager={this.uploadManager} />
          </Snackbar>
          <SnackbarView
            open={!!this.state.snackbarMessage}
            error={!!this.state.snackbarError}
            onClose={this.snackbarClose}
          >
            {this.state.snackbarMessage}
          </SnackbarView>
        </div>
      );
    }
  },
);
